import React, { useState, useEffect, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  discoverWebhookPatterns,
  getSliceAutomationStatus,
  enableSliceGroupAutomation,
  getSliceSyncActivity,
  discoverFuneralHomes,
  createSliceGroupWithSlice,
  CreateSliceGroupParams,
  WebhookPattern,
  SliceGroupAutomationStatus,
  SliceSyncActivity,
  FuneralHomeDiscovered
} from '../../../../../mogul/core/_requests'
import { APIError } from '../../../../../app/modules/auth'
import usePartners from '../../../../../mogul/hooks/usePartners'
import useAccounts from '../../../../../mogul/hooks/useAccounts'
import { formatLeadItemDateBetter } from '../DebugPipedrive'
import { EntityDiscovery } from './EntityDiscovery'

interface PatternDiscoveryProps {
  partnerId?: number
}

interface FuneralHomeDiscoveryProps {
  partnerId?: number
}

// Temporary mapping - will be replaced with unique_identifier lookup
const PARTNER_ID_MAP: Record<string, number> = {
  'part_y11RjKnVIEobU3ck': 7,  // Signature Guestbook
}

function FuneralHomeDiscovery({ partnerId }: FuneralHomeDiscoveryProps) {
  const navigate = useNavigate()
  const [loading, setLoading] = useState(false)
  const [funeralHomes, setFuneralHomes] = useState<FuneralHomeDiscovered[]>([])
  const [error, setError] = useState<string | null>(null)
  const [lookbackDays, setLookbackDays] = useState(30)
  const [creatingSliceFor, setCreatingSliceFor] = useState<string | null>(null)
  const [confirmingSliceFor, setConfirmingSliceFor] = useState<string | null>(null)
  
  const { partners } = usePartners(true)
  // Default to Signature Guestbook (ID: 7)
  const [selectedPartnerId, setSelectedPartnerId] = useState<number | undefined>(partnerId || 7)

  const loadFuneralHomes = useCallback(async () => {
    if (!selectedPartnerId) return
    
    setLoading(true)
    setError(null)
    try {
      const result = await discoverFuneralHomes(selectedPartnerId, lookbackDays)
      
      // Ensure result is an array
      if (!Array.isArray(result)) {
        setFuneralHomes([])
        setError('Invalid response format from server')
        return
      }
      
      // Validate each funeral home object
      const validatedHomes = result.filter(home => 
        home && 
        typeof home.webhook_count === 'number' &&
        typeof home.has_slice === 'boolean'
      )
      
      if (validatedHomes.length !== result.length) {
        console.warn('Some funeral homes had invalid data and were filtered out')
      }
      
      setFuneralHomes(validatedHomes)
    } catch (err) {
      console.error('Funeral home discovery error:', err)
      const errorMessage = err instanceof APIError ? err.message : 'Failed to discover funeral homes'
      setError(errorMessage)
      setFuneralHomes([])
    } finally {
      setLoading(false)
    }
  }, [selectedPartnerId, lookbackDays])

  // Auto-load funeral homes when partner is selected or lookback period changes
  useEffect(() => {
    if (selectedPartnerId) {
      loadFuneralHomes()
    }
  }, [selectedPartnerId, lookbackDays, loadFuneralHomes])

  const createSliceGroup = async (funeralHome: FuneralHomeDiscovered) => {
    const sliceGroupName = `SGB Leads` // Standard name for all SGB slice groups
    
    setCreatingSliceFor(funeralHome.unique_identifier)
    setConfirmingSliceFor(null)
    
    try {
      const params: CreateSliceGroupParams = {
        partnerId: selectedPartnerId!, // We know this is set because button is disabled otherwise
        accountId: 2, // Premier account ID - TODO: make this configurable
        funeralHomeName: funeralHome.name,
        funeralHomeIdentifier: funeralHome.unique_identifier,
        sliceGroupName: sliceGroupName
      }

      const result = await createSliceGroupWithSlice(params)
      
      if (result.status === 'success') {
        // Navigate to the slice settings page with a success notice
        const sliceId = result.slice.unique_identifier
        navigate(`/debugging/all-leads/webhooks/slices/${sliceId}/settings`, {
          state: {
            justCreated: true,
            funeralHomeName: funeralHome.name
          }
        })
      } else {
        alert(`Failed to create slice: ${result.message || 'Unknown error'}`)
      }
    } catch (error) {
      console.error('Error creating slice group:', error)
      alert(`Error creating slice: ${error instanceof Error ? error.message : 'Unknown error'}`)
    } finally {
      setCreatingSliceFor(null)
      setConfirmingSliceFor(null)
    }
  }

  return (
    <div className="card">
      <div className="card-header">
        <h3 className="card-title">Funeral Home Discovery</h3>
        <div className="card-toolbar">
          <button 
            className="btn btn-sm btn-primary"
            onClick={loadFuneralHomes}
            disabled={loading || !selectedPartnerId}
          >
            {loading ? 'Loading...' : 'Refresh Funeral Homes'}
          </button>
        </div>
      </div>
      
      <div className="card-body">
        <div className="row mb-5">
          <div className="col-md-6">
            <label className="form-label">Partner</label>
            <select 
              className="form-select"
              value={selectedPartnerId || ''}
              onChange={(e) => setSelectedPartnerId(e.target.value ? parseInt(e.target.value) : undefined)}
            >
              <option value="">Select Partner</option>
              {partners.map(partner => {
                const partnerId = PARTNER_ID_MAP[partner.unique_identifier]
                if (!partnerId) {
                  console.warn('No ID mapping for partner:', partner.name, partner.unique_identifier)
                  return null
                }
                return (
                  <option key={partner.unique_identifier} value={partnerId}>
                    {partner.name}
                  </option>
                )
              }).filter(Boolean)}
            </select>
          </div>
          
          <div className="col-md-6">
            <label className="form-label">Lookback Period</label>
            <select 
              className="form-select"
              value={lookbackDays}
              onChange={(e) => setLookbackDays(parseInt(e.target.value))}
            >
              <option value={7}>Last 7 days</option>
              <option value={14}>Last 2 weeks</option>
              <option value={30}>Last 30 days</option>
              <option value={60}>Last 2 months</option>
              <option value={90}>Last 3 months</option>
            </select>
          </div>
        </div>

        {error && (
          <div className="alert alert-danger">{error}</div>
        )}

        {!selectedPartnerId && (
          <div className="alert alert-info">
            <i className="fas fa-home me-2"></i>
            Select a partner to discover funeral homes that have sent webhook data recently.
          </div>
        )}

        {funeralHomes.length > 0 && (
          <>
            <div className="row mb-5">
              <div className="col-md-3">
                <div className="card border border-gray-300">
                  <div className="card-body text-center">
                    <div className="fw-bold fs-2 text-gray-800">{funeralHomes.length || 0}</div>
                    <div className="text-muted">Total Funeral Homes</div>
                  </div>
                </div>
              </div>
              <div className="col-md-3">
                <div className="card border border-warning">
                  <div className="card-body text-center">
                    <div className="fw-bold fs-2 text-warning">{funeralHomes.filter(h => h && !h.has_slice).length || 0}</div>
                    <div className="text-muted">Need Setup</div>
                  </div>
                </div>
              </div>
              <div className="col-md-3">
                <div className="card border border-success">
                  <div className="card-body text-center">
                    <div className="fw-bold fs-2 text-success">{funeralHomes.filter(h => h && h.has_slice).length || 0}</div>
                    <div className="text-muted">Configured</div>
                  </div>
                </div>
              </div>
              <div className="col-md-3">
                <div className="card border border-danger">
                  <div className="card-body text-center">
                    <div className="fw-bold fs-2 text-danger">{funeralHomes.filter(h => h && !h.has_slice && typeof h.webhook_count === 'number' && h.webhook_count >= 50).length || 0}</div>
                    <div className="text-muted">High Priority</div>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="table-responsive">
            <table className="table table-row-dashed">
              <thead>
                <tr>
                  <th>Funeral Home</th>
                  <th>Recent Activity</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {funeralHomes.map((home, idx) => {
                  const priority = home.webhook_count >= 50 ? 'high' : home.webhook_count >= 10 ? 'medium' : 'low'
                  const priorityColor = priority === 'high' ? 'danger' : priority === 'medium' ? 'warning' : 'secondary'
                  
                  return (
                    <tr key={idx} className={[
                      !home.has_slice ? 'table-warning' : '',
                      confirmingSliceFor === home.unique_identifier ? 'bg-light-primary' : ''
                    ].filter(Boolean).join(' ')}>
                      <td>
                        <div className="d-flex align-items-center">
                          {!home.has_slice && (
                            <i className="fas fa-exclamation-triangle text-warning me-2" title="Needs Setup"></i>
                          )}
                          <div>
                            <div className="fw-bold">{home.name || 'Unnamed Funeral Home'}</div>
                            <div className="text-muted small">
                              <code>{home.unique_identifier}</code>
                            </div>
                          </div>
                        </div>
                      </td>
                      <td>
                        <div>
                          <span className={`badge badge-light-${priorityColor} me-2`}>
                            {home.webhook_count} webhooks
                          </span>
                          {priority === 'high' && (
                            <span className="badge badge-light-danger me-2">High Priority</span>
                          )}
                          {home.last_webhook_at && (
                            <div className="text-muted small">
                              Last: {formatLeadItemDateBetter(home.last_webhook_at)}
                            </div>
                          )}
                        </div>
                      </td>
                      <td>
                        {home.has_slice ? (
                          <span className="badge badge-light-success">
                            <i className="fas fa-check me-1"></i>
                            Configured ({home.slice_group_name})
                          </span>
                        ) : (
                          <span className="badge badge-light-warning">
                            <i className="fas fa-exclamation-triangle me-1"></i>
                            Needs Setup
                          </span>
                        )}
                      </td>
                      <td>
                        {!home.has_slice && (
                          <>
                            {confirmingSliceFor === home.unique_identifier ? (
                              <div className="d-flex gap-2">
                                <button 
                                  className="btn btn-sm btn-success"
                                  onClick={() => createSliceGroup(home)}
                                  disabled={creatingSliceFor === home.unique_identifier}
                                >
                                  {creatingSliceFor === home.unique_identifier ? (
                                    <>
                                      <span className="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></span>
                                      Creating...
                                    </>
                                  ) : (
                                    <>
                                      <i className="fas fa-check me-1"></i>
                                      Confirm
                                    </>
                                  )}
                                </button>
                                <button 
                                  className="btn btn-sm btn-light"
                                  onClick={() => setConfirmingSliceFor(null)}
                                  disabled={creatingSliceFor === home.unique_identifier}
                                >
                                  Cancel
                                </button>
                              </div>
                            ) : (
                              <button 
                                className="btn btn-sm btn-primary"
                                onClick={() => setConfirmingSliceFor(home.unique_identifier)}
                              >
                                <i className="fas fa-plus me-1"></i>
                                Create Slice
                              </button>
                            )}
                          </>
                        )}
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
          </>
        )}

        {selectedPartnerId && !loading && funeralHomes.length === 0 && !error && (
          <div className="alert alert-warning">
            <i className="fas fa-search me-2"></i>
            No funeral homes found for the selected timeframe. Try increasing the lookback period or check if webhooks are being received.
          </div>
        )}
      </div>
    </div>
  )
}

function PatternDiscovery({ partnerId }: PatternDiscoveryProps) {
  const [loading, setLoading] = useState(false)
  const [patterns, setPatterns] = useState<WebhookPattern[]>([])
  const [error, setError] = useState<string | null>(null)
  const [lookbackHours, setLookbackHours] = useState(168)
  
  const { partners } = usePartners(true)
  const [selectedPartnerId, setSelectedPartnerId] = useState<number | undefined>(partnerId)
  

  const loadPatterns = useCallback(async () => {
    if (!selectedPartnerId) return
    
    setLoading(true)
    setError(null)
    try {
      const result = await discoverWebhookPatterns(selectedPartnerId, lookbackHours)
      setPatterns(result)
    } catch (err) {
      const errorMessage = err instanceof APIError ? err.message : 'Failed to discover patterns'
      setError(errorMessage)
    } finally {
      setLoading(false)
    }
  }, [selectedPartnerId, lookbackHours])

  return (
    <div className="card">
      <div className="card-header">
        <h3 className="card-title">Pattern Discovery</h3>
        <div className="card-toolbar">
          <button 
            className="btn btn-sm btn-primary"
            onClick={loadPatterns}
            disabled={loading || !selectedPartnerId}
          >
            {loading ? 'Discovering...' : 'Discover Patterns'}
          </button>
        </div>
      </div>
      
      <div className="card-body">
        <div className="row mb-5">
          <div className="col-md-6">
            <label className="form-label">Partner</label>
            <select 
              className="form-select"
              value={selectedPartnerId || ''}
              onChange={(e) => setSelectedPartnerId(e.target.value ? parseInt(e.target.value) : undefined)}
            >
              <option value="">Select Partner</option>
              {partners.map(partner => (
                <option key={partner.id} value={partner.id}>
                  {partner.name}
                </option>
              ))}
            </select>
          </div>
          
          <div className="col-md-6">
            <label className="form-label">Lookback Hours</label>
            <select 
              className="form-select"
              value={lookbackHours}
              onChange={(e) => setLookbackHours(parseInt(e.target.value))}
            >
              <option value={1}>Last 1 hour</option>
              <option value={6}>Last 6 hours</option>
              <option value={24}>Last 24 hours</option>
              <option value={48}>Last 2 days</option>
              <option value={168}>Last 7 days</option>
            </select>
          </div>
        </div>

        {error && (
          <div className="alert alert-danger">{error}</div>
        )}

        {!selectedPartnerId && (
          <div className="alert alert-info">
            <i className="fas fa-info-circle me-2"></i>
            Select a partner and click "Discover Patterns" to analyze webhook data for common patterns that could be automated.
          </div>
        )}

        {selectedPartnerId && !loading && patterns.length === 0 && !error && (
          <div className="alert alert-warning">
            <i className="fas fa-search me-2"></i>
            No patterns found for the selected timeframe. Try increasing the lookback period to 7 days to find recurring patterns in the webhook data.
          </div>
        )}

        {patterns.length > 0 && (
          <div className="table-responsive">
            <table className="table table-row-dashed">
              <thead>
                <tr>
                  <th>Field Path</th>
                  <th>Occurrences</th>
                  <th>Suggested Operator</th>
                  <th>Top Values</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {patterns.map((pattern, idx) => (
                  <tr key={idx}>
                    <td>
                      <code>{pattern.field}</code>
                    </td>
                    <td>{pattern.total_occurrences}</td>
                    <td>
                      <span className="badge badge-light-primary">
                        {pattern.suggested_operator}
                      </span>
                    </td>
                    <td>
                      {pattern.top_values.slice(0, 3).map((value, vidx) => (
                        <div key={vidx} className="mb-1">
                          <span className="text-muted">
                            "{value.value}" ({value.percentage}%)
                          </span>
                        </div>
                      ))}
                    </td>
                    <td>
                      <button 
                        className="btn btn-sm btn-light-primary"
                        title="Create slice from this pattern"
                      >
                        Create Slice
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {patterns.length === 0 && !loading && !error && selectedPartnerId && (
          <div className="text-center text-muted py-5">
            No patterns discovered. Try selecting a different time range or partner.
          </div>
        )}
      </div>
    </div>
  )
}

interface AutomationStatusProps {
  accountId?: number
}

function AutomationStatus({ accountId }: AutomationStatusProps) {
  const [loading, setLoading] = useState(false)
  const [status, setStatus] = useState<SliceGroupAutomationStatus[]>([])
  const [error, setError] = useState<string | null>(null)
  const [enablingGroup, setEnablingGroup] = useState<string | null>(null)
  
  const { accounts } = useAccounts()
  const [selectedAccountId, setSelectedAccountId] = useState<number | undefined>(accountId)

  const loadStatus = useCallback(async () => {
    if (!selectedAccountId) return
    
    setLoading(true)
    setError(null)
    try {
      const result = await getSliceAutomationStatus(selectedAccountId)
      setStatus(result)
    } catch (err) {
      const errorMessage = err instanceof APIError ? err.message : 'Failed to load automation status'
      setError(errorMessage)
    } finally {
      setLoading(false)
    }
  }, [selectedAccountId])

  const handleEnableAutomation = async (sliceGroupId: number, groupName: string) => {
    const frmLocationId = prompt(`Enter GHL Location ID to link all slices in "${groupName}":`)
    if (!frmLocationId) return

    setEnablingGroup(sliceGroupId.toString())
    try {
      const result = await enableSliceGroupAutomation(sliceGroupId, frmLocationId)
      alert(`Successfully enabled automation for ${result.successful} out of ${result.total} slices`)
      loadStatus() // Reload status
    } catch (err) {
      const errorMessage = err instanceof APIError ? err.message : 'Failed to enable automation'
      alert(errorMessage)
    } finally {
      setEnablingGroup(null)
    }
  }

  useEffect(() => {
    if (selectedAccountId) {
      loadStatus()
    }
  }, [selectedAccountId, loadStatus])

  return (
    <div className="card">
      <div className="card-header">
        <h3 className="card-title">Slice Group Automation Status</h3>
      </div>
      
      <div className="card-body">
        <div className="mb-5">
          <label className="form-label">Account</label>
          <select 
            className="form-select"
            value={selectedAccountId || ''}
            onChange={(e) => setSelectedAccountId(e.target.value ? parseInt(e.target.value) : undefined)}
          >
            <option value="">Select Account</option>
            {accounts.map(account => (
              <option key={account.id} value={account.id}>
                {account.name}
              </option>
            ))}
          </select>
        </div>

        {error && (
          <div className="alert alert-danger">{error}</div>
        )}

        {loading && (
          <div className="text-center py-5">
            <div className="spinner-border" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
          </div>
        )}

        {status.length > 0 && (
          <div className="table-responsive">
            <table className="table table-row-dashed">
              <thead>
                <tr>
                  <th>Slice Group</th>
                  <th>Total Slices</th>
                  <th>Automated</th>
                  <th>Coverage</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {status.map((group) => (
                  <tr key={group.slice_group.unique_identifier}>
                    <td>{group.slice_group.name}</td>
                    <td>{group.total_slices}</td>
                    <td>
                      {group.automated_slices} / {group.total_slices}
                    </td>
                    <td>
                      <div className="d-flex align-items-center">
                        <div className="progress w-100px me-2">
                          <div 
                            className="progress-bar bg-primary" 
                            style={{ width: `${group.automation_percentage || 0}%` }}
                          />
                        </div>
                        <span>{group.automation_percentage || 0}%</span>
                      </div>
                    </td>
                    <td>
                      {group.fully_automated ? (
                        <span className="badge badge-light-success">
                          Fully Automated
                        </span>
                      ) : (
                        <span className="badge badge-light-warning">
                          Partial
                        </span>
                      )}
                    </td>
                    <td>
                      {!group.fully_automated && (
                        <button 
                          className="btn btn-sm btn-light-primary"
                          onClick={() => handleEnableAutomation(
                            parseInt(group.slice_group.unique_identifier.split('_')[1]), 
                            group.slice_group.name
                          )}
                          disabled={enablingGroup === group.slice_group.unique_identifier}
                        >
                          {enablingGroup === group.slice_group.unique_identifier ? 'Enabling...' : 'Enable All'}
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {status.length === 0 && !loading && !error && selectedAccountId && (
          <div className="text-center text-muted py-5">
            No slice groups found for this account.
          </div>
        )}
      </div>
    </div>
  )
}

interface SyncActivityMonitorProps {
  partnerId?: number
}

function SyncActivityMonitor({ partnerId }: SyncActivityMonitorProps) {
  const [loading, setLoading] = useState(false)
  const [activity, setActivity] = useState<SliceSyncActivity | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [hours, setHours] = useState(24)
  
  const { partners } = usePartners(true)
  const [selectedPartnerId, setSelectedPartnerId] = useState<number | undefined>(partnerId)

  const loadActivity = useCallback(async () => {
    if (!selectedPartnerId) return
    
    setLoading(true)
    setError(null)
    try {
      const result = await getSliceSyncActivity(selectedPartnerId, hours)
      setActivity(result)
    } catch (err) {
      const errorMessage = err instanceof APIError ? err.message : 'Failed to load sync activity'
      setError(errorMessage)
    } finally {
      setLoading(false)
    }
  }, [selectedPartnerId, hours])

  useEffect(() => {
    if (selectedPartnerId) {
      loadActivity()
    }
  }, [selectedPartnerId, hours, loadActivity])

  return (
    <div className="card">
      <div className="card-header">
        <h3 className="card-title">Sync Activity Monitor</h3>
        <div className="card-toolbar">
          <button 
            className="btn btn-sm btn-icon btn-light-primary"
            onClick={loadActivity}
            disabled={loading || !selectedPartnerId}
            title="Refresh"
          >
            <i className="bi bi-arrow-clockwise"></i>
          </button>
        </div>
      </div>
      
      <div className="card-body">
        <div className="row mb-5">
          <div className="col-md-6">
            <label className="form-label">Partner</label>
            <select 
              className="form-select"
              value={selectedPartnerId || ''}
              onChange={(e) => setSelectedPartnerId(e.target.value ? parseInt(e.target.value) : undefined)}
            >
              <option value="">Select Partner</option>
              {partners.map(partner => (
                <option key={partner.id} value={partner.id}>
                  {partner.name}
                </option>
              ))}
            </select>
          </div>
          
          <div className="col-md-6">
            <label className="form-label">Time Period</label>
            <select 
              className="form-select"
              value={hours}
              onChange={(e) => setHours(parseInt(e.target.value))}
            >
              <option value={1}>Last 1 hour</option>
              <option value={6}>Last 6 hours</option>
              <option value={24}>Last 24 hours</option>
              <option value={48}>Last 2 days</option>
              <option value={168}>Last 7 days</option>
            </select>
          </div>
        </div>

        {error && (
          <div className="alert alert-danger">{error}</div>
        )}

        {loading && (
          <div className="text-center py-5">
            <div className="spinner-border" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
          </div>
        )}

        {activity && (
          <>
            <div className="d-flex justify-content-between mb-5">
              <div className="d-flex flex-column">
                <span className="fs-2 fw-bold">{activity.total_synced}</span>
                <span className="text-muted">Total Synced</span>
              </div>
              
              <div className="d-flex flex-column text-end">
                <span className="fs-2 fw-bold">
                  {activity.recent_syncs.length > 0 
                    ? formatLeadItemDateBetter(activity.recent_syncs[0].inserted_at)
                    : 'N/A'
                  }
                </span>
                <span className="text-muted">Last Sync</span>
              </div>
            </div>

            {activity.recent_syncs.length > 0 && (
              <>
                <h4 className="mb-3">Recent Syncs</h4>
                <div className="table-responsive">
                  <table className="table table-row-dashed">
                    <thead>
                      <tr>
                        <th>Time</th>
                        <th>Partner</th>
                        <th>Data</th>
                        <th>GHL Contact</th>
                      </tr>
                    </thead>
                    <tbody>
                      {activity.recent_syncs.slice(0, 10).map((sync) => (
                        <tr key={sync.unique_identifier}>
                          <td>{formatLeadItemDateBetter(sync.inserted_at)}</td>
                          <td>{sync.partner.name}</td>
                          <td>
                            <details>
                              <summary className="cursor-pointer">View Data</summary>
                              <pre className="mt-2" style={{ fontSize: '0.8rem' }}>
                                {JSON.stringify(sync.normalized_data, null, 2)}
                              </pre>
                            </details>
                          </td>
                          <td>
                            {sync.private_data.ghl_contact ? (
                              <a 
                                href={`https://app.premier360platform.com/v2/location/${sync.private_data.ghl_contact.ghl_location_id}/contacts/detail/${sync.private_data.ghl_contact.ghl_contact_id}`}
                                target="_blank"
                                rel="noreferrer"
                              >
                                View in GHL
                              </a>
                            ) : (
                              <span className="text-muted">Not synced</span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            )}
          </>
        )}
      </div>
    </div>
  )
}

export function WebhookSliceAutomation() {
  return (
    <div className="py-5">
      <EntityDiscovery />
    </div>
  )
}

// Keep original components for backward compatibility
export { FuneralHomeDiscovery, PatternDiscovery, AutomationStatus, SyncActivityMonitor }